import Aura from '@primeuix/themes/aura';
import type { Preset } from '@primeuix/themes/types';
import type { ThemePreset } from './theme.types';

/**
 * Lookup table from preset name to the underlying PrimeNG preset config.
 *
 * Only Aura is authored. Lara/Nora runtime switching was retired with the
 * theme configurator.
 */
export const PRESETS: Record<ThemePreset, Preset> = {
  Aura: Aura,
};

/**
 * Flat map of color-name → palette, as exposed by a PrimeNG preset's
 * `primitive` section (e.g. `{ emerald: { 50: '#...', ... }, ... }`).
 */
export type PrimitiveMap = Record<string, Record<string, string>>;

/**
 * Returns the primitive color palettes for a preset, merging in any
 * brand-specific palettes supplied by the brand layer.
 *
 * @param presetName - Preset to read primitives from.
 * @param brandPalettes - Additional named palettes to merge on top. Keys in
 *   this map override the preset's built-in palettes with the same name.
 */
export function getPrimitive(
  presetName: ThemePreset,
  brandPalettes: Record<string, Record<string, string>>
): PrimitiveMap | undefined {
  const preset = PRESETS[presetName] as Record<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- PrimeNG preset records expose `primitive` as `unknown`; we narrow at the boundary
  const built = preset['primitive'] as PrimitiveMap | undefined;
  if (!built) {
    return { ...brandPalettes };
  }
  return { ...built, ...brandPalettes };
}

/**
 * Builds the PrimeNG preset extension that layers primary-color semantics
 * (including the `noir` greyscale variant and light/dark color schemes) on
 * top of Aura.
 *
 * Dark highlight `color` / `focusColor` use PrimeNG Aura's standard
 * near-white text on translucent primary — intentional platform convention,
 * not a brand token.
 *
 * @param _preset - Kept for call-site compatibility; always Aura.
 * @param primaryColorName - Selected primary color name (e.g. `'indutrust'`).
 *   The special value `'noir'` produces a greyscale-derived primary.
 * @param primaryPalette - Palette map for the selected color. Ignored when
 *   `primaryColorName` is `'noir'`.
 */
export function buildPresetExtension(
  _preset: ThemePreset,
  primaryColorName: string,
  primaryPalette: Record<string, string> | undefined
): Record<string, unknown> {
  if (primaryColorName === 'noir') {
    return {
      semantic: {
        primary: {
          50: '{surface.50}',
          100: '{surface.100}',
          200: '{surface.200}',
          300: '{surface.300}',
          400: '{surface.400}',
          500: '{surface.500}',
          600: '{surface.600}',
          700: '{surface.700}',
          800: '{surface.800}',
          900: '{surface.900}',
          950: '{surface.950}',
        },
        colorScheme: {
          light: {
            primary: {
              color: '{primary.950}',
              contrastColor: '#ffffff',
              hoverColor: '{primary.800}',
              activeColor: '{primary.700}',
            },
            highlight: {
              background: '{primary.950}',
              focusBackground: '{primary.700}',
              color: '#ffffff',
              focusColor: '#ffffff',
            },
          },
          dark: {
            primary: {
              color: '{primary.50}',
              contrastColor: '{primary.950}',
              hoverColor: '{primary.200}',
              activeColor: '{primary.300}',
            },
            highlight: {
              background: '{primary.50}',
              focusBackground: '{primary.300}',
              color: '{primary.950}',
              focusColor: '{primary.950}',
            },
          },
        },
      },
    };
  }

  // Aura authored default
  return {
    semantic: {
      primary: primaryPalette,
      colorScheme: {
        light: {
          primary: {
            color: '{primary.500}',
            contrastColor: '#ffffff',
            hoverColor: '{primary.600}',
            activeColor: '{primary.700}',
          },
          highlight: {
            background: '{primary.50}',
            focusBackground: '{primary.100}',
            color: '{primary.700}',
            focusColor: '{primary.800}',
          },
        },
        dark: {
          primary: {
            color: '{primary.400}',
            contrastColor: '{surface.900}',
            hoverColor: '{primary.300}',
            activeColor: '{primary.200}',
          },
          highlight: {
            background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
            focusBackground:
              'color-mix(in srgb, {primary.400}, transparent 76%)',
            // Aura dark-mode highlight text (PrimeNG convention).
            color: 'color-mix(in srgb, {surface.0}, transparent 13%)',
            focusColor: 'color-mix(in srgb, {surface.0}, transparent 13%)',
          },
        },
      },
    },
  };
}
